import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, ArrowRight, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://scalemicrofin.scalebot.in';

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const token = searchParams.get('token') || searchParams.get('access_token');

  useEffect(() => {
    // Check if token exists
    if (!token) {
      toast.error('Invalid or missing reset token');
      setTokenValid(false);
      setTimeout(() => navigate('/login'), 3000);
    }
  }, [token, navigate]);

  const validatePassword = () => {
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('token', token);
      formDataToSend.append('new_password', formData.password);

      await axios.post(`${API_URL}/api/auth/reset-password`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(true);
      toast.success('Password reset successful! Redirecting to login...');

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        'Failed to reset password. The link may have expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-800">Invalid Reset Link</h2>
              <p className="text-gray-600">
                This password reset link is invalid or has expired.
                Please request a new one.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-emerald-600 to-teal-600"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-800">Password Reset Successful!</h2>
              <p className="text-gray-600">
                Your password has been successfully reset. You can now login with your new password.
              </p>
              <p className="text-sm text-gray-500">Redirecting to login page...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-200 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <Card className="glass card-hover shadow-strong border-0">
          <CardHeader className="space-y-2 pb-6">
            <div className="flex items-center justify-center mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_scalemicrofin/artifacts/zwx3wriv_scaleMicroFin-icon%27png.png"
                alt="JD Group Logo"
                className="h-16 w-16 drop-shadow-lg"
              />
            </div>
            <CardTitle className="text-3xl font-bold gradient-text text-center">
              Reset Your Password
            </CardTitle>
            <CardDescription className="text-base text-center">
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                  New Password
                </Label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                    <Lock className="text-gray-400" size={20} />
                  </div>
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    className="pl-12 pr-14 h-12 form-input text-base"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                  />
                  <div className="absolute right-3 top-1/2 z-20" style={{ transform: 'translateY(-50%)' }}>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-emerald-600 focus:outline-none"
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff size={20} strokeWidth={2} className="flex-shrink-0" />
                      ) : (
                        <Eye size={20} strokeWidth={2} className="flex-shrink-0" />
                      )}
                    </button>
                  </div>
                </div>
                {formData.password && (
                  <PasswordStrengthIndicator password={formData.password} />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-700">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                    <Lock className="text-gray-400" size={20} />
                  </div>
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    className="pl-12 pr-14 h-12 form-input text-base"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    minLength={8}
                  />
                  <div className="absolute right-3 top-1/2 z-20" style={{ transform: 'translateY(-50%)' }}>
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-emerald-600 focus:outline-none"
                      tabIndex={-1}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} strokeWidth={2} className="flex-shrink-0" />
                      ) : (
                        <Eye size={20} strokeWidth={2} className="flex-shrink-0" />
                      )}
                    </button>
                  </div>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle size={14} />
                    Passwords do not match
                  </p>
                )}
              </div>

              <div className="hidden md:block bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Password requirements:</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>At least 8 characters long</li>
                    <li>Mix of uppercase and lowercase letters</li>
                    <li>Include numbers and special characters</li>
                  </ul>
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold text-base shadow-lg hover:shadow-xl"
                disabled={loading || formData.password !== formData.confirmPassword}
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    Reset Password
                    <ArrowRight className="ml-2" size={20} />
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors text-sm"
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ResetPassword;
